import { useParams, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { films, categories } from '@/mocks/films';
import type { Film } from '@/mocks/films';

const categoryMap: Record<string, string> = {
  drama: '电视剧',
  movie: '电影',
  variety: '综艺',
  anime: '动漫',
};

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();

  const categoryInfo = categories.find(c => c.slug === slug);
  const categoryName = slug === 'all' ? '全部影视' : (categoryMap[slug] || slug);

  const filteredFilms = useMemo(() => {
    if (slug === 'all') return films;
    return films.filter((f: Film) => f.category === slug);
  }, [slug]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `木瓜影视 - ${categoryName}`,
    description: `木瓜影视${categoryName}分类，免费在线观看最新${categoryName}，高清不缓冲，即点即播。`,
    url: `https://www.muguays.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '木瓜影视',
      url: 'https://www.muguays.com',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEOHead
        title={`${categoryName} - 木瓜影视 | 免费在线观看最新${categoryName}`}
        description={`木瓜影视${categoryName}频道，免费在线观看最新最热${categoryName}，高清画质流畅不缓冲，即点即播，${categoryName}资源每日更新。`}
        keywords={`${categoryName},在线观看,免费${categoryName},木瓜影视,高清不缓冲,即点即播,最新${categoryName}`}
        canonicalPath={`/category/${slug}`}
        ogTitle={`木瓜影视 - ${categoryName} | 免费在线高清观看`}
        ogDescription={`木瓜影视${categoryName}频道，海量${categoryName}资源免费在线观看，高清流畅即点即播。`}
        schema={categorySchema}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
            {categoryName}
          </h1>
          <p className="text-foreground-500 text-sm">
            共 {filteredFilms.length} 部{categoryName}资源，高清在线观看
          </p>
        </div>

        <nav aria-label="分类筛选" className="flex items-center gap-2 mb-8 flex-wrap">
          <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </nav>

        {filteredFilms.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {filteredFilms.map((film: Film) => (
              <MovieCard key={film.id} film={film} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
            <Link
              to="/category/all"
              className="inline-block mt-3 text-primary-500 hover:text-primary-600 text-sm font-medium cursor-pointer"
            >
              查看全部影视
            </Link>
          </div>
        )}
      </section>
    </>
  );
}